import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { calculateBMI, getAssessmentBMI } from "../services/api";
import { getQuestions } from "../lib/questionsCache";
import banner2 from "../assets/photos/bannerClinics.png";
import { useLocation, useNavigate } from 'react-router-dom';
import ResearchLayout from './ResearchLayout';
import Modal from './Modal';
import AIAssessmentViewer from "./AIAssessmentViewer"; // Add this import
import HealthMeter from './HealthMeter';
import Legend from './Legend';
import { convertMoodValue } from '../utils/moodConverter';
import MeasurementDisplay from './MeasurementDisplay';
import useUnitPreferences from '../hooks/useUnitPreferences';

// Add color mapping for health status colors
const colorMap = {
  "#83D65C": "Excellent",
  "#5BC0DE": "Good",
  "#ffe366": "Good",
  "#ff944d": "Fair",
  "#ff6666": "High Risk",
  "#d9534f": "Critical"
};

// Function to get health status from color
const getStatusFromColor = (color) => {
  return colorMap[color] || color || "Unknown";
};

// Convert color hex or status to display text
const convertColorToText = (value) => {
  if (!value) return 'Unknown';
  if (typeof value === 'string' && value.startsWith('#')) {
    return getStatusFromColor(value);
  }
  return value;
};

const ResearchAnswerQuestions = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const protocolId = params.get('protocol');
  const caseId = params.get('case');
  const batchId = params.get('batch');
  const startQuestionId = params.get('question');

  const { user } = useAuth();
  const { isInternationalSystem } = useUnitPreferences();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answeredMap, setAnsweredMap] = useState({});
  const [submittedMap, setSubmittedMap] = useState({});
  const [protocolInfo, setProtocolInfo] = useState(null);
  const [caseDetails, setCaseDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const modalRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate(); // Add this line
  const [scrolledToProfile, setScrolledToProfile] = useState(false);
  const [nextCase, setNextCase] = useState(null);
  const [bmiValue, setBmiValue] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [currentElapsedSeconds, setCurrentElapsedSeconds] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [assignmentStatus, setAssignmentStatus] = useState('active'); // 'active' or 'completed'
  const [isSubmittingCase, setIsSubmittingCase] = useState(false);

  // State for batch expiry modal
  const [showBatchExpiredModal, setShowBatchExpiredModal] = useState(false);
  const [batchExpiredMessage, setBatchExpiredMessage] = useState('');

  // Fetch questions and answers
  useEffect(() => {
    if (protocolId && caseId) {
      // CRITICAL: Validate batch access before loading anything
      if (batchId) {
        validateBatchAccess();
      } else {
        // No batch specified - load normally
        fetchQuestions();
        fetchCaseDetails();
        fetchAssignmentStatus();
      }
    }
    // eslint-disable-next-line
  }, [protocolId, caseId, batchId, location.search]);

  // CRITICAL: Validate that batch is not expired before allowing access
  const validateBatchAccess = async () => {
    try {
      console.log(`[ResearchAnswerQuestions] Validating batch access - case=${caseId}, batch=${batchId}`);
      const response = await api.get(`/batches/${batchId}/case/${caseId}/access-check`);
      
      if (response.data.access_granted) {
        console.log(`[ResearchAnswerQuestions] ✅ Access granted - loading questions`);
        fetchQuestions();
        fetchCaseDetails();
        fetchAssignmentStatus();
      }
    } catch (error) {
      console.error(`[ResearchAnswerQuestions] ❌ Access validation failed:`, error);
      
      // Check if batch is expired
      if (error.response?.status === 403) {
        const errorData = error.response.data;
        if (errorData.error === 'Batch expired') {
          console.error(`[ResearchAnswerQuestions]   BLOCKED: Batch is expired`);
          setLoading(false);
          setBatchExpiredMessage(`Batch "${errorData.batch_name}" has expired.\n\n${errorData.message}\n\nYou will be redirected to Review Cases.`);
          setShowBatchExpiredModal(true);
          return;
        }
      }
      
      // Other errors
      setLoading(false);
      setError(error.response?.data?.message || `Failed to validate batch access: ${error.message}`);
      console.error(`[ResearchAnswerQuestions] Error message:`, setError);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    setError("");
    try {
      // Use cached questions instead of direct API call
      const questionsResponse = await getQuestions(protocolId);
      const questionsData = questionsResponse.questions || [];
      const answersResponse = await api.get(`/answers?protocol_id=${protocolId}&case_id=${caseId}`);

      const answersData = answersResponse.data.answers || [];

      setQuestions(questionsData);

      // Build answered and submitted maps by question ID
      const initialAnswers = {};
      const initialSubmitted = {};
      questionsData.forEach((q) => {
        const found = answersData.find(a => a.question_id === q.id);
        // Set default values based on question type
        let defaultValue = "";
        if (q.type === "range" && !found) {
          defaultValue = "1"; // Default range value
        }
        initialAnswers[q.id] = found ? found.content : defaultValue;
        initialSubmitted[q.id] = !!found;
      });
      setAnsweredMap(initialAnswers);
      setSubmittedMap(initialSubmitted);

      // Load protocol info
      try {
        const protocolRes = await api.get(`/protocols/${protocolId}`);
        setProtocolInfo(protocolRes.data.protocol);
      } catch (err) {
        console.log("Could not load protocol info");
      }
    } catch (err) {
      setError("Failed to load questions. Please try again.");
      console.error("Error fetching questions:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDetails = async () => {
    try {
      const response = await api.get(`/cases/${caseId}?include_answers=true`);
      setCaseDetails(response.data.case);
    } catch (err) {
      console.error("Error loading case details:", err);
    }
  };

  const fetchAssignmentStatus = async () => {
    try {
      const response = await api.get(`/assignments?doctor_id=${user.id}&protocol_id=${protocolId}&case_id=${caseId}`);
      const assignments = response.data.assignments || [];
      if (assignments.length > 0) {
        const assignment = assignments[0];
        setAssignmentStatus(assignment.status || 'active');
        
        // NOTE: Batch expiration is already checked via validateBatchAccess() before loading
        // No need to redundantly check here - if we got here, the backend already validated the batch is not expired
      }
    } catch (err) {
      console.error("Error loading assignment status:", err);
      // Default to active if we can't fetch status
      setAssignmentStatus('active');
    }
  };

  // When case details load, scroll the left panel to the "Complete Health Profile" section once
  useEffect(() => {
    if (!caseDetails || scrolledToProfile) return;
    // profileRef is inside the scrollable .app-card-body; scrollIntoView will use that as the ancestor
    if (profileRef.current && typeof profileRef.current.scrollIntoView === 'function') {
      try {
        profileRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setScrolledToProfile(true);
      } catch (e) {
        // fallback: no-op
        setScrolledToProfile(true);
      }
    }
  }, [caseDetails, scrolledToProfile]);

  // Calculate BMI when case details are loaded
  useEffect(() => {
    const calculateCaseBMI = async () => {
      if (!caseDetails || !caseDetails.height || !caseDetails.weight) {
        console.log('No case details or missing height/weight data');
        setBmiValue(null);
        return;
      }

      console.log('Calculating BMI for case details:', {
        height: caseDetails.height,
        heightUnit: caseDetails.heightUnit,
        weight: caseDetails.weight,
        weightUnit: caseDetails.weightUnit
      });

      try {
        const bmi = await calculateCaseBMIValue(
          caseDetails.height,
          caseDetails.heightUnit,
          caseDetails.weight,
          caseDetails.weightUnit
        );
        console.log('BMI calculated successfully:', bmi);
        setBmiValue(bmi);
      } catch (error) {
        console.error('Error calculating case BMI:', error);
        setBmiValue(null);
      }
    };

    calculateCaseBMI();
  }, [caseDetails]);

  // Set current index to the next unanswered question or to the one from query param
  useEffect(() => {
    if (questions.length === 0) return;
    // If a startQuestionId is provided, jump to it
    if (startQuestionId) {
      const idx = questions.findIndex(q => String(q.id) === String(startQuestionId));
      if (idx >= 0) {
        setCurrentIndex(idx);
        return;
      }
    }
    // Otherwise, jump to the first unanswered question, or stay on current if all are answered
    const firstUnansweredIdx = questions.findIndex(q => !submittedMap[q.id]);
    const allQuestionsAnswered = questions.every(q => submittedMap[q.id]);

    if (allQuestionsAnswered) {
      // If all questions are answered, stay on the current question to show Submit Case button
      // Don't change currentIndex
    } else {
      setCurrentIndex(firstUnansweredIdx >= 0 ? firstUnansweredIdx : 0);
    }
    // eslint-disable-next-line
  }, [startQuestionId, questions, submittedMap]);

  // PRECISE TIMER: Start timer IMMEDIATELY when current question changes - NO DELAY
  useEffect(() => {
    if (questions.length > 0 && currentIndex >= 0 && questions[currentIndex]) {
      console.log('Starting PRECISE timer for question:', questions[currentIndex].id, 'at:', new Date().toISOString());
      
      // Clear any existing timer interval first
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      
      // Get current question details
      const questionId = questions[currentIndex].id;
      const sessionKey = `question_timer_${protocolId}_${caseId}_${questionId}`;
      
      // CRITICAL FIX: Clean up ALL previous question timers to prevent time carryover
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (key.startsWith('question_timer_') && key !== sessionKey) {
          console.log('CLEANUP: Removing old timer key:', key);
          localStorage.removeItem(key);
          localStorage.removeItem(`${key}_elapsed`);
        }
      });
      
      // Check if we have an existing timer (for page refreshes)
      let existingStartTime = localStorage.getItem(sessionKey);
      let startTime;
      
      if (existingStartTime) {
        // Only resume if it's for the EXACT same question and time is reasonable (less than 2 hours)
        try {
          const storedTime = new Date(existingStartTime);
          const timeDiff = (new Date() - storedTime) / 1000; // seconds
          
          if (timeDiff > 0 && timeDiff < 7200) { // Less than 2 hours
            startTime = storedTime;
            console.log('Resuming timer from localStorage at:', startTime.toISOString(), `(${timeDiff}s elapsed)`);
          } else {
            console.log('TIMER TOO OLD, starting fresh:', timeDiff, 'seconds');
            startTime = new Date();
            localStorage.setItem(sessionKey, startTime.toISOString());
          }
        } catch (e) {
          console.log('Invalid stored timer, starting fresh');
          startTime = new Date();
          localStorage.setItem(sessionKey, startTime.toISOString());
        }
      } else {
        // Start completely new timer
        startTime = new Date();
        localStorage.setItem(sessionKey, startTime.toISOString());
        console.log('NEW TIMER started at:', startTime.toISOString());
      }
      
      setQuestionStartTime(startTime);
      
      // Start precise second-by-second timer
      const interval = setInterval(() => {
        const now = new Date();
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        setCurrentElapsedSeconds(elapsedSeconds);
        
        // Persist current elapsed time
        localStorage.setItem(`${sessionKey}_elapsed`, elapsedSeconds.toString());
      }, 1000); // Update every second for precise tracking
      
      setTimerInterval(interval);
    }
  }, [currentIndex, questions.length]);

  // Stop timer when moving to next question to capture exact time spent
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
    };
  }, [currentIndex]);

  // Start time tracking session when component mounts
  useEffect(() => {
    if (protocolId && caseId && user) {
      console.log('Starting time tracking session for case:', caseId, 'protocol:', protocolId);
      startTimeTrackingSession();
    }
    
    // Cleanup: stop time tracking when component unmounts
    return () => {
      if (currentSession) {
        console.log('Cleaning up time tracking session');
        stopTimeTrackingSession();
      }
    };
    // eslint-disable-next-line
  }, [protocolId, caseId, user]);

  // Keep the original function for backward compatibility
  const startQuestionTimer = () => {
    const now = new Date();
    console.log('Question timer started at:', now.toISOString());
    setQuestionStartTime(now);
  };

  // Start a time tracking session
  const startTimeTrackingSession = async () => {
    try {
      const response = await api.post('/answers/start-tracking', {
        case_id: caseId,
        protocol_id: protocolId
      });
      
      if (response.data.success) {
        setCurrentSession(response.data.session);
      }
    } catch (error) {
      console.error('Failed to start time tracking session:', error);
    }
  };

  // Stop time tracking session
  const stopTimeTrackingSession = async () => {
    try {
      if (currentSession) {
        await api.post('/answers/stop-tracking', {
          case_id: caseId,
          protocol_id: protocolId
        });
        setCurrentSession(null);
      }
    } catch (error) {
      console.error('Failed to stop time tracking session:', error);
    }
  };

  // PRECISE Calculate time spent on a question using exact tracking
  const calculateQuestionTime = () => {
    const currentQ = questions[currentIndex];
    if (!currentQ) return null;
    
    const questionId = currentQ.id;
    const sessionKey = `question_timer_${protocolId}_${caseId}_${questionId}`;
    
    // Get precise elapsed time from localStorage
    const elapsedStr = localStorage.getItem(`${sessionKey}_elapsed`);
    const elapsedSeconds = elapsedStr ? parseInt(elapsedStr, 10) : 0;
    
    // Also calculate as backup in case localStorage is corrupted
    const now = new Date();
    const timeDiff = Math.floor((now - questionStartTime) / 1000);
    
    // Use the more precise localStorage value, fallback to calculation
    const finalElapsed = Math.max(elapsedSeconds, timeDiff);
    
    console.log('PRECISE time calculation:', {
      questionId,
      localStorageElapsed: elapsedSeconds,
      calculatedElapsed: timeDiff,
      finalElapsed
    });
    
    return finalElapsed;
  };

  const handleAnswerChange = (value) => {
    const currentQ = questions[currentIndex];
    const newMap = { ...answeredMap };
    newMap[currentQ.id] = value;
    setAnsweredMap(newMap);
  };

  const handleNext = async () => {
    const currentQ = questions[currentIndex];
    const answer = answeredMap[currentQ.id];

    // Only submit if not already submitted
    if (!submittedMap[currentQ.id]) {
      // Validation for text questions
      if (currentQ.type === "text") {
        if (!answer) {
          setModalMessage("Please provide an answer before proceeding.");
          setShowModal(true);
          return;
        }
        const wordCount = answer.trim().split(/\s+/).filter(word => word.length > 0).length;
        if (wordCount > 20) {
          setModalMessage("Your answer must not exceed 20 words.");
          setShowModal(true);
          return;
        }
      }
      // Validation for range questions
      if (currentQ.type === "range") {
        const numValue = parseInt(answer);
        if (!answer || isNaN(numValue) || numValue < 1 || numValue > 100) {
          setModalMessage("Please select a value between 1 and 100.");
          setShowModal(true);
          return;
        }
      }
      // Validation for color scale questions
      if (currentQ.type === "color_scale") {
        const validColors = ['red', 'orange', 'yellow', 'green'];
        if (!answer || !validColors.includes(answer)) {
          setModalMessage("Please select a color option.");
          setShowModal(true);
          return;
        }
      }
      if (!answer) {
        setModalMessage("Please select an answer before proceeding.");
        setShowModal(true);
        return;
      }
      try {
        const questionStartedAt = questionStartTime ? questionStartTime.toISOString() : null;
        const questionEndedAt = new Date().toISOString();
        const totalTimeSeconds = calculateQuestionTime();
        
        console.log('PAYMENT-READY: Submitting answer with PRECISE time tracking:', {
          questionId: currentQ.id,
          questionStartedAt,
          questionEndedAt,
          totalTimeSeconds: `${totalTimeSeconds} seconds`,
          answer
        });
        
        await api.post('/answers', {
          question_id: currentQ.id,
          protocol_id: protocolId,
          case_id: caseId,
          answer_text: answer,
          question_started_at: questionStartedAt,
          question_ended_at: questionEndedAt,
          total_time_seconds: totalTimeSeconds
        });
        setSubmittedMap((prev) => ({ ...prev, [currentQ.id]: true }));
        
        console.log('PAYMENT-READY: Answer submitted successfully with precise time tracking');
        
        // Clean up timer for this question (since it's answered)
        const sessionKey = `question_timer_${protocolId}_${caseId}_${currentQ.id}`;
        localStorage.removeItem(sessionKey);
        localStorage.removeItem(`${sessionKey}_elapsed`);
        
        // Also clean up any other timer data to prevent cross-contamination
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(key => {
          if (key.startsWith('question_timer_') && !key.includes(currentQ.id.toString())) {
            localStorage.removeItem(key);
            localStorage.removeItem(`${key}_elapsed`);
          }
        });
        
        // Stop current timer interval
        if (timerInterval) {
          clearInterval(timerInterval);
          setTimerInterval(null);
        }
        
        // Trigger progress report update by dispatching a custom event
        window.dispatchEvent(new CustomEvent('questionAnswered', {
          detail: {
            questionId: currentQ.id,
            protocolId: protocolId,
            caseId: caseId,
            answeredBy: user?.id,
            timeSpent: totalTimeSeconds
          }
        }));
      } catch (err) {
        setModalMessage("Failed to submit answer. Please try again.");
        setShowModal(true);
        console.error("Answer submission error:", err);
        return;
      }
    }

    // Check if all questions are now answered
    const allQuestionsAnswered = questions.every(q => submittedMap[q.id]);

    // Move to next unanswered question - precise timer will automatically start for next question
    let nextIdx = currentIndex + 1;
    if (nextIdx < questions.length && !allQuestionsAnswered) {
      setCurrentIndex(nextIdx);
    } else {
      // Clean up all timers for this session when all questions are done
      const sessionKey = `question_timer_${protocolId}_${caseId}_${currentQ.id}`;
      localStorage.removeItem(sessionKey);
      localStorage.removeItem(`${sessionKey}_elapsed`);

      // If all questions are answered and assignment is still active, stay on current question
      // to show the "Submit Case" button. Don't navigate or show modal.
      if (allQuestionsAnswered && assignmentStatus === 'active') {
        // Stay on current question - UI will show "Submit Case" button
        return;
      }

      // Only check for more cases if assignment is already completed
      if (assignmentStatus === 'completed') {
        checkForMoreCases();
      }
    }
  };

  const checkForMoreCases = async () => {
    try {
      // Get continue assignments to see if there are more cases
      const response = await api.get('/doctors/continue-progress');
      const assignments = response.data.assignments || response.data || [];
      
      // Filter out current case
      const otherCases = assignments.filter(a =>
        !(a.protocol_id == protocolId && a.case_id == caseId)
      );
      
      // Store next case for direct navigation
      setNextCase(otherCases.length > 0 ? otherCases[0] : null);
      
      // Always show simple completion with both buttons
      setModalMessage("Questions answered successfully!");
      setShowModal(true);
    } catch (error) {
      console.error('Error checking for more cases:', error);
      // Fallback to simple completion
      setNextCase(null);
      setModalMessage("Questions answered successfully!");
      setShowModal(true);
    }
  };

  const getRedirectPath = () => {
    // If batch ID is available, redirect to batches page with batch parameter
    // This allows the doctor to return to the exact batch they were working on
    if (batchId) {
      return `/doctor/batches?batch=${encodeURIComponent(batchId)}`;
    }
    // Fallback to review cases if no batch ID
    return '/doctor/review-cases';
  };

  const handleReviewQuestions = () => {
    // Navigate to first question for review
    setCurrentIndex(0);
    setShowModal(false);
  };

  const handleContinueReviewingCases = () => {
    // Navigate to batches page (with batch parameter if available)
    navigate(getRedirectPath());
  };

  const handleSubmitCase = async () => {
    try {
      setIsSubmittingCase(true);
      const response = await api.post('/answers/submit-case', {
        case_id: caseId,
        protocol_id: protocolId
      });

      if (response.data.success) {
        setAssignmentStatus('completed');
        setModalMessage("Case submitted successfully! All answers are now locked.");
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error submitting case:', error);
      setModalMessage("Failed to submit case. Please try again.");
      setShowModal(true);
    } finally {
      setIsSubmittingCase(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getHealthColor = (prediction) => {
    switch (prediction?.toLowerCase()) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-info';
      case 'fair': return 'text-warning';
      case 'at risk':
      case 'needs attention':
      case 'high risk':
      case 'critical': return 'text-danger';
      default: return 'text-muted';
    }
  };

  // Calculate BMI using backend endpoint (local function for this component)
  // Check if inputs should be disabled (never disable for doctors)
  const isInputDisabled = (questionId) => {
    return false;
  };

  const calculateCaseBMIValue = async (height, heightUnit, weight, weightUnit) => {
    if (!height || !weight) return null;

    try {
      const response = await calculateBMI(height, heightUnit, weight, weightUnit);
      return response.bmi;
    } catch (error) {
      console.error('Error calculating BMI:', error);
      // Fallback to local calculation if backend fails
      try {
        let heightInMeters = height;
        let weightInKg = weight;

        // Convert height to meters
        if (heightUnit === 'cm') {
          heightInMeters = height / 100;
        } else if (heightUnit === 'inches' || heightUnit === 'in') {
          heightInMeters = height * 0.0254;
        } else if (heightUnit === 'feet' || heightUnit === 'ft') {
          heightInMeters = height * 0.3048;
        }

        // Convert weight to kg
        if (weightUnit === 'lbs' || weightUnit === 'pounds') {
          weightInKg = weight * 0.453592;
        } else if (weightUnit === 'stones') {
          weightInKg = weight * 6.35029;
        }

        // Calculate BMI
        const bmi = weightInKg / (heightInMeters * heightInMeters);
        return bmi ? bmi.toFixed(1) : null;
      } catch (fallbackError) {
        console.error('Fallback BMI calculation also failed:', fallbackError);
        return null;
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <p className="text-danger text-center">{error}</p>;
  }

  // If all questions are answered and case is submitted, show completion modal
  const allAnswered = questions.length > 0 && questions.every(q => submittedMap[q.id]);
  if (!questions.length) {
    return (
      <Modal
        isOpen={true}
        onClose={() => navigate(getRedirectPath())}
        title="All Done!"
        onConfirm={() => navigate(getRedirectPath())}
        confirmText="Okay"
      >
        <div className="text-success text-center fs-4">Questions answered successfully!</div>
      </Modal>
    );
  }

  const currentQ = questions[currentIndex];

  return (
    <ResearchLayout pageTitle="Review Cases" hideHeader={true} hideFooter={true}>
      <div className="my-5 px-3">
        <div className="app-card-header bg-primary text-white" style={{ marginTop: '-60px' }}>
            <h5 className="mb-0">
              <i className="fas fa-clipboard-list me-2"></i>
              {protocolInfo?.name || 'Protocol Questions'}
            </h5>
        </div>
        <div 
          className="row" 
          // style={{ marginTop: '-60px' }}
        >
          {/* Left: Assessment/Case Details */}
          <div className="col-lg-8">
            <div className="app-card h-100" style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 160px)' }}>
              {/* <div className="app-card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-clipboard-list me-2"></i>
                  {protocolInfo?.name || 'Protocol Questions'}
                </h5>
              </div> */}
              <div className="app-card-body" style={{ overflowY: 'auto', flex: '1 1 auto' }}>
                {/* Sticky header: protocol info, case id, progress */}
                <div style={{
                  position: 'sticky',
                  top: '-23.85px',
                  // Make nearly-opaque to hide content behind the sticky header
                  background: 'rgba(255,255,255,0.98)',
                  zIndex: 20,
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  // Keep a subtle blur when supported but rely on opacity to obscure text
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                }}>
                  {/* <p className="mb-2"><strong>Protocol:</strong> {protocolInfo?.name}</p> */}
                  {/* <p className="text-muted small mb-0">{protocolInfo?.description}</p> */}
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-2 mt-3">
                        <strong>Case ID:</strong> {caseDetails?.Id || caseId}
                      </p>
                      <p className="mb-0">
                        <strong>Progress:</strong>
                        <span className={`ms-2 badge ${allAnswered ? 'bg-success' : 'bg-info'}`}>
                          {allAnswered ? 'All Answered' : `${currentIndex + 1} of ${questions.length}`}
                        </span>
                      </p>
                    </div>
                    <div className="ms-3">
                      <Legend />
                    </div>
                  </div>
                </div>
                {/* Case Details - scrollable below sticky header */}
                {caseDetails && (
                  <div className="border-top" ref={profileRef} style={{ marginTop: 0, paddingTop: '0.25rem' }}>
                    <h6 className="text-primary mb-3">Complete Health Profile</h6>
                    <div className="row" style={{ fontSize: '13px' }}>
                      {/* Column 1: two stacked cards */}
                      <div className={`${protocolInfo?.show_ai_response ? 'col-md-3' : 'col-md-6'} mb-3`}>
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-header bg-light">
                            <h6 className="mb-0"><i className="fas fa-info-circle me-2"></i>Basic Information</h6>
                          </div>
                          <div className="card-body">
                            
                            <p className="mb-1"><strong>Age:</strong> {caseDetails.age}</p>
                            <p className="mb-0"><strong>Gender:</strong> {caseDetails.gender}</p>
                          </div>
                        </div>

                        <div className="card border-0 shadow-sm">
                          <div className="card-body">
                            <h6 className="fw-bold mb-3 text-primary">
                              <i className="fas fa-heartbeat me-2"></i>
                              Vital Signs
                            </h6>
                            <p className="mb-1"><strong>Blood Pressure:</strong> {caseDetails.systolicBloodPressure || 'N/A'}/{caseDetails.diastolicBloodPressure || 'N/A'} mmHg</p>
                            <p className="mb-1"><strong>Resting Heart Rate:</strong> {caseDetails.restingHeartRate/10 || 'N/A'} bpm</p>
                            <p className="mb-1"><strong>Blood Glucose:</strong> <MeasurementDisplay measurementType="blood_glucose" value={caseDetails.bloodGlucose} originalUnit={caseDetails.bloodGlucoseUnit} showLabel={false} /></p>
                            <p className="mb-1"><strong>Oxygen Saturation:</strong> {caseDetails.oxygenSaturationLevel || 'N/A'}%</p>

                            <h6 className="fw-bold mb-3 mt-4 text-primary">
                              <i className="fas fa-ruler me-2"></i>
                              Physical Measurements
                            </h6>
                            <p className="mb-1"><strong>Height:</strong> <MeasurementDisplay measurementType="height" value={caseDetails.height} originalUnit={caseDetails.heightUnit} showLabel={false} /></p>
                            <p className="mb-1"><strong>Weight:</strong> <MeasurementDisplay measurementType="weight" value={caseDetails.weight} originalUnit={caseDetails.weightUnit} showLabel={false} /></p>
                            <p className="mb-1"><strong>BMI:</strong> {bmiValue || 'N/A'}</p>
                            <p className="mb-1"><strong>Waist Circumference:</strong> <MeasurementDisplay measurementType="circumference" value={caseDetails.waist} originalUnit={caseDetails.waistUnit} showLabel={false} /></p>
                            <p className="mb-0"><strong>Hip Circumference:</strong> <MeasurementDisplay measurementType="circumference" value={caseDetails.hip} originalUnit={caseDetails.hipUnit} showLabel={false} /></p>
                          </div>
                        </div>
                      </div>

                      {/* Column 2: two stacked cards */}
                      <div className={`${protocolInfo?.show_ai_response ? 'col-md-3' : 'col-md-6'} mb-3`}>
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-header bg-light">
                            <h6 className="mb-0"><i className="fas fa-running me-2"></i>Lifestyle</h6>
                          </div>
                          <div className="card-body">
                            <p className="mb-1">
                              <strong>Activity Level:</strong> {caseDetails.activeDailyLifestyleLevel}/10
                            </p>
                            <p className="mb-1"><strong>Stress Level:</strong> {caseDetails.stressFrequency === 'Never' || caseDetails.stressFrequency === '0' || caseDetails.stressFrequency === 0 ? 'Never' :
                              caseDetails.stressFrequency === 'Rarely' || caseDetails.stressFrequency === 'Rare' || caseDetails.stressFrequency === '1' || caseDetails.stressFrequency === 1 ? 'Sometimes' :
                              caseDetails.stressFrequency === 'Sometimes' || caseDetails.stressFrequency === '2' || caseDetails.stressFrequency === 2 ? 'Many times' :
                              caseDetails.stressFrequency === 'Often' || caseDetails.stressFrequency === 'Very Often' || caseDetails.stressFrequency === '3' || caseDetails.stressFrequency === 3 ? 'Always' :
                              caseDetails.stressFrequency}</p>
                            <p className="mb-1"><strong>Sleep Quality:</strong> {caseDetails.wellSleepLevel}/10</p>
                            <p className="mb-1"><strong>Loss of Interest Level:</strong> {caseDetails.lossOfInterestAppetiteFrequency === 'Never' || caseDetails.lossOfInterestAppetiteFrequency === '0' || caseDetails.lossOfInterestAppetiteFrequency === 0 ? 'Never' :
                              caseDetails.lossOfInterestAppetiteFrequency === 'Rarely' || caseDetails.lossOfInterestAppetiteFrequency === 'Rare' || caseDetails.lossOfInterestAppetiteFrequency === '1' || caseDetails.lossOfInterestAppetiteFrequency === 1 ? 'Sometimes' :
                              caseDetails.lossOfInterestAppetiteFrequency === 'Sometimes' || caseDetails.lossOfInterestAppetiteFrequency === '2' || caseDetails.lossOfInterestAppetiteFrequency === 2 ? 'Many times' :
                              caseDetails.lossOfInterestAppetiteFrequency === 'Often' || caseDetails.lossOfInterestAppetiteFrequency === 'Very Often' || caseDetails.lossOfInterestAppetiteFrequency === '3' || caseDetails.lossOfInterestAppetiteFrequency === 3 ? 'Always' :
                              caseDetails.lossOfInterestAppetiteFrequency}</p>
                            <p className="mb-1"><strong>Low Energy Level:</strong> {caseDetails.fatigueFrequency === 'Never' || caseDetails.fatigueFrequency === '0' || caseDetails.fatigueFrequency === 0 ? 'Never' :
                              caseDetails.fatigueFrequency === 'Rarely' || caseDetails.fatigueFrequency === 'Rare' || caseDetails.fatigueFrequency === '1' || caseDetails.fatigueFrequency === 1 ? 'Sometimes' :
                              caseDetails.fatigueFrequency === 'Sometimes' || caseDetails.fatigueFrequency === '2' || caseDetails.fatigueFrequency === 2 ? 'Many times' :
                              caseDetails.fatigueFrequency === 'Often' || caseDetails.fatigueFrequency === 'Very Often' || caseDetails.fatigueFrequency === '3' || caseDetails.fatigueFrequency === 3 ? 'Always' :
                              caseDetails.fatigueFrequency}</p>
                            <p className="mb-1"><strong>Mood:</strong> {convertMoodValue(caseDetails.mood)}</p>
                            <p className="mb-1">
                              <strong>Smoker:</strong> {caseDetails.isSmoker ? 'Yes' : 'No'}
                              {caseDetails.isSmoker &&
                                <span> ({caseDetails.cigarettesPerDay} per day, {caseDetails.yearsSmoked} years)</span>}
                            </p>
                            <p className="mb-0">
                              <strong>Alcohol Consumption:</strong> {caseDetails.isDrinker ? 'Yes' : 'No'}
                              {caseDetails.isDrinker &&
                                <span> ({caseDetails.bottlesPerDay} per day, {caseDetails.drinkingDaysPerWeek} days/week)</span>}
                            </p>
                          </div>
                        </div>

                        <div className="card border-0 shadow-sm">
                          <div className="card-header bg-light">
                            <h6 className="mb-0"><i className="fas fa-notes-medical me-2"></i>Medical History</h6>
                          </div>
                          <div className="card-body">
                            <p className="mb-1">
                              <strong>Individual Chronic Disease History:</strong> {(caseDetails.diagnosedChronicDisease === 1 || caseDetails.diagnosedChronicDisease === '1') ? 'Yes' : (caseDetails.diagnosedChronicDisease === 0 || caseDetails.diagnosedChronicDisease === '0') ? 'No' : 'No'}
                            </p>
                            <p className="mb-1">
                              <strong>Family Chronic Disease History:</strong> {(caseDetails.familyMemberDiagnosedChronicDisease === 1 || caseDetails.familyMemberDiagnosedChronicDisease === '1') ? 'Yes' : (caseDetails.familyMemberDiagnosedChronicDisease === 0 || caseDetails.familyMemberDiagnosedChronicDisease === '0') ? 'No' : 'No'}
                            </p>
                            <p className="mb-1"><strong>Education:</strong> {caseDetails.highestEducation}</p>
                            <p className="mb-1"><strong>Experience Pain:</strong> {(caseDetails.lastMonthPain === 1 || caseDetails.lastMonthPain === '1') ? 'Yes' : (caseDetails.lastMonthPain === 0 || caseDetails.lastMonthPain === '0') ? 'No' : 'No'}</p>
                            <p className="mb-1"><strong>Experience Loss of Vision:</strong> {(caseDetails.lastMonthDiminishedVision === 1 || caseDetails.lastMonthDiminishedVision === '1') ? 'Yes' : (caseDetails.lastMonthDiminishedVision === 0 || caseDetails.lastMonthDiminishedVision === '0') ? 'No' : 'No'}</p>
                            <p className="mb-0"><strong>Experience Loss of Hearing:</strong> {(caseDetails.lastMonthDiminishedHearing === 1 || caseDetails.lastMonthDiminishedHearing === '1') ? 'Yes' : (caseDetails.lastMonthDiminishedHearing === 0 || caseDetails.lastMonthDiminishedHearing === '0') ? 'No' : 'No'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Column 3: AI Assessment - Only show if enabled by protocol */}
                      {protocolInfo?.show_ai_response && (
                        <div className="col-md-6 mb-3">
                          <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-light">
                              <h6 className="mb-0"><i className="fas fa-notes-medical me-2"></i>Evaluation Results</h6>
                            </div>
                            <div className="card-body d-flex align-items-stretch">
                              <div style={{ width: '100%' }}>
                                {caseDetails?.aiResponseData ? (
                                  <AIAssessmentViewer data={caseDetails.aiResponseData} />
                                ) : (
                                  <span className="text-muted">No Evaluation Results available.</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Right: Question Card */}
          <div className="col-lg-4">
            <div className="card shadow-sm border-0 p-4 mb-4 bg-white rounded">
              <p className="text-center mb-4">
                Question <strong>{currentIndex + 1}</strong> of <strong>{questions.length}</strong>
              </p>
              <div className="question-box p-4 border rounded bg-light shadow-sm">
                <h6 className="text-muted">Category: {currentQ.category || 'General'}</h6>
                <h5>Type: {currentQ.type === "mcq"
                  ? "Multiple Choice"
                  : currentQ.type === "boolean"
                  ? "True/False"
                  : currentQ.type === "range"
                  ? "Range Selection"
                  : currentQ.type === "color_scale"
                  ? "Color Scale"
                  : "Text"}</h5>
                <div className="card mb-4 shadow-sm border-0">
                  <div className="card-body">
                    <p className="fs-5 mt-3 fw-bold mb-0">{currentQ.text}</p>
                  </div>
                </div>
                {currentQ.type === "mcq" && (
                  <div className="list-group mb-3">
                    {currentQ.choices?.map((choice, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                          answeredMap[currentQ.id] === choice ? "active bg-primary text-white" : ""
                        }`}
                        onClick={() => handleAnswerChange(choice)}
                        disabled={isInputDisabled(currentQ.id)}
                      >
                        {choice}
                        {answeredMap[currentQ.id] === choice && (
                          <span className="badge bg-success">Selected</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {currentQ.type === "boolean" && (
                  <div className="d-flex justify-content-center mb-3">
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn ${answeredMap[currentQ.id] === "True" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => handleAnswerChange("True")}
                        disabled={isInputDisabled(currentQ.id)}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className={`btn ${answeredMap[currentQ.id] === "False" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => handleAnswerChange("False")}
                        disabled={isInputDisabled(currentQ.id)}
                      >
                        No
                      </button>
                    </div>
                  </div>
                )}
                {currentQ.type === "text" && (
                  <div className="mb-3">
                    <textarea
                      className="form-control"
                      value={answeredMap[currentQ.id] || ""}
                      onChange={e => {
                        if (isInputDisabled(currentQ.id)) return; // Prevent editing if case completed
                        const val = e.target.value;
                        handleAnswerChange(val);
                      }}
                      placeholder="Type your answer here (maximum 20 words)..."
                      readOnly={isInputDisabled(currentQ.id)}
                    />
                    <div className="form-text">
                      {(() => {
                        const currentAnswer = answeredMap[currentQ.id] || "";
                        const wordCount = currentAnswer.trim().split(/\s+/).filter(word => word.length > 0).length;
                        const isOverLimit = wordCount > 20;
                        return (
                          <span className={isOverLimit ? "text-danger" : "text-muted"}>
                            {wordCount}/20 words {isOverLimit ? "(exceeds limit)" : ""}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {currentQ.type === "range" && (
                  <div className="mb-3">
                    <div className="text-center mb-3">
                      <h4 className="text-primary">{answeredMap[currentQ.id] || 1}</h4>
                      <small className="text-muted">Selected Value (1-100)</small>
                    </div>
                    
                    {/* Slider */}
                    <div className="mb-3">
                      <input
                        type="range"
                        className="form-range"
                        min="1"
                        max="100"
                        step="1"
                        value={answeredMap[currentQ.id] || 1}
                        onChange={e => {
                          if (isInputDisabled(currentQ.id)) return; // Prevent editing if case completed
                          handleAnswerChange(e.target.value);
                        }}
                        disabled={isInputDisabled(currentQ.id)}
                        style={{ width: '100%' }}
                      />
                      <div className="d-flex justify-content-between text-muted small">
                        <span>1</span>
                        <span>25</span>
                        <span>50</span>
                        <span>75</span>
                        <span>100</span>
                      </div>
                    </div>
                    
                    {/* Plus/Minus Buttons */}
                    <div className="d-flex justify-content-center align-items-center gap-3">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          if (isInputDisabled(currentQ.id)) return;
                          const currentValue = parseInt(answeredMap[currentQ.id] || 1);
                          if (currentValue > 1) {
                            handleAnswerChange(String(currentValue - 1));
                          }
                        }}
                        disabled={isInputDisabled(currentQ.id) || (parseInt(answeredMap[currentQ.id] || 1) <= 1)}
                      >
                        <i className="fas fa-minus"></i>
                      </button>
                      
                      <div className="text-center" style={{ minWidth: '60px' }}>
                        <input
                          type="number"
                          className="form-control form-control-sm text-center"
                          min="1"
                          max="100"
                          value={answeredMap[currentQ.id] || 1}
                          onChange={e => {
                            if (isInputDisabled(currentQ.id)) return;
                            let value = parseInt(e.target.value);
                            if (isNaN(value)) value = 1;
                            if (value < 1) value = 1;
                            if (value > 100) value = 100;
                            handleAnswerChange(String(value));
                          }}
                          disabled={isInputDisabled(currentQ.id)}
                          style={{ width: '70px' }}
                        />
                      </div>
                      
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          if (isInputDisabled(currentQ.id)) return;
                          const currentValue = parseInt(answeredMap[currentQ.id] || 1);
                          if (currentValue < 100) {
                            handleAnswerChange(String(currentValue + 1));
                          }
                        }}
                        disabled={isInputDisabled(currentQ.id) || (parseInt(answeredMap[currentQ.id] || 1) >= 100)}
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>
                )}
                {currentQ.type === "color_scale" && (
                  <div className="mb-3">
                    <div className="text-center mb-3">
                      <small className="text-muted">Select a color that best represents your answer</small>
                    </div>
                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                      {[
                        { value: 'red', color: '#dc3545', textColor: 'white' },
                        { value: 'orange', color: '#fd7e14', textColor: 'white' },
                        { value: 'yellow', color: '#ffc107', textColor: 'black' },
                        { value: 'green', color: '#198754', textColor: 'white' }
                      ].map((colorOption) => (
                        <button
                          key={colorOption.value}
                          type="button"
                          className={`btn d-flex align-items-center justify-content-center ${
                            answeredMap[currentQ.id] === colorOption.value ? 'border-dark' : 'border-light'
                          }`}
                          style={{
                            backgroundColor: colorOption.color,
                            color: colorOption.textColor,
                            padding: '15px 25px',
                            borderRadius: '8px',
                            fontWeight: 'bold',
                            minWidth: '90px',
                            minHeight: '60px',
                            borderWidth: answeredMap[currentQ.id] === colorOption.value ? '3px' : '2px',
                            textTransform: 'capitalize',
                            position: 'relative'
                          }}
                          onClick={() => handleAnswerChange(colorOption.value)}
                          disabled={isInputDisabled(currentQ.id)}
                        >
                          {colorOption.value}
                          {answeredMap[currentQ.id] === colorOption.value && (
                            <i className="fas fa-check-circle position-absolute"
                               style={{
                                 top: '-5px',
                                 right: '-5px',
                                 backgroundColor: 'white',
                                 color: colorOption.color,
                                 borderRadius: '50%',
                                 fontSize: '16px'
                               }}></i>
                          )}
                        </button>
                      ))}
                    </div>
                    {answeredMap[currentQ.id] && (
                      <div className="text-center mt-3">
                        <span className="badge bg-success">
                          <i className="fas fa-check me-1"></i>
                          Selected: {answeredMap[currentQ.id].charAt(0).toUpperCase() + answeredMap[currentQ.id].slice(1)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="d-flex justify-content-between mt-4">
                  <button
                    className="btn btn-outline-secondary"
                    disabled={currentIndex === 0}
                    onClick={() => setCurrentIndex(currentIndex - 1)}
                  >
                    Previous
                  </button>
                  {allAnswered ? (
                    <button
                      className="btn btn-primary"
                      onClick={handleSubmitCase}
                      disabled={isSubmittingCase}
                    >
                      {isSubmittingCase ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Submit Case
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      className="btn btn-success"
                      onClick={handleNext}
                      disabled={!answeredMap[currentQ.id]}
                    >
                      {currentIndex === questions.length - 1 ? (submittedMap[currentQ.id] ? "Next" : "Submit & Next") : (submittedMap[currentQ.id] ? "Next" : "Submit & Next")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Completion Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            if (modalMessage === "Questions answered successfully!" || modalMessage === "case_completed_with_more" || modalMessage === "Case submitted successfully! All answers are now locked.") {
              // Redirect to batch if available, otherwise to review cases
              navigate(getRedirectPath());
            } else {
              setShowModal(false);
            }
          }}
          title={
            modalMessage === "Questions answered successfully!" ? "All Done!" :
            modalMessage === "case_completed_with_more" ? "Case Completed!" :
            modalMessage === "Case submitted successfully! All answers are now locked." ? "Case Submitted" :
            "Validation Error"
          }
          onConfirm={
            modalMessage === "Questions answered successfully!" ? undefined :
            modalMessage === "case_completed_with_more" ? undefined :
            undefined
          }
          confirmText={
            modalMessage === "Questions answered successfully!" ? undefined :
            undefined
          }
        >
          {modalMessage === "case_completed_with_more" ? (
            <div className="text-center">
              <div className="text-success fs-4 mb-4">
                <i className="fas fa-check-circle me-2"></i>
                Case completed successfully!
              </div>
              <p className="text-muted mb-4">
                You have more cases waiting to be reviewed. What would you like to do next?
              </p>
              <div className="d-flex gap-3 justify-content-center">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleReviewQuestions}
                >
                  <i className="fas fa-eye me-2"></i>
                  Review Questions
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleContinueReviewingCases}
                >
                  <i className="fas fa-forward me-2"></i>
                  Continue Reviewing Cases
                </button>
              </div>
            </div>
          ) : modalMessage === "Questions answered successfully!" ? (
            <div className="text-center">
              <div className="text-success fs-4 mb-4">
                <i className="fas fa-check-circle me-2"></i>
                All questions completed successfully!
              </div>
              <div className="d-flex gap-3 justify-content-center">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigate('/doctor/dashboard')}
                >
                  <i className="fas fa-home me-2"></i>
                  Okay
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleContinueReviewingCases}
                >
                  <i className="fas fa-forward me-2"></i>
                  Continue Reviewing
                </button>
              </div>
            </div>
          ) : modalMessage === "Case submitted successfully! All answers are now locked." ? (
            <div className="text-center">
              <div className="text-success fs-4 mb-4">
                <i className="fas fa-check-circle me-2"></i>
                Case submitted successfully!
              </div>
              <p className="text-muted mb-4">
                All answers have been locked and the case is now complete.
              </p>
              <div className="d-flex gap-3 justify-content-center">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(getRedirectPath())}
                >
                  <i className="fas fa-list me-2"></i>
                  {batchId ? 'Back to Batch' : 'Back to Cases'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-danger">
              {modalMessage}
            </div>
          )}
        </Modal>

        {/* Batch Expired Modal */}
        <Modal
          isOpen={showBatchExpiredModal}
          title="  Cannot Access Case"
          onClose={() => {
            navigate(getRedirectPath());
          }}
        >
          <div className="text-center">
            {batchExpiredMessage.split('\n').map((line, index) => (
              <p key={index} className="mb-2">{line}</p>
            ))}
          </div>
        </Modal>
      </div>
    </ResearchLayout>
  );
};

export default ResearchAnswerQuestions;